// Add Student: the shared form, a duplicate check on the mobile number, and a save that
// returns to the list. The same form component backs the Edit drawer.
import { useState } from 'react';

import { StudentFormFields } from 'src/front-components/lead-desk/student-form';
import { Notice } from 'src/front-components/lead-desk/kit';
import type { DeskContext } from 'src/front-components/lead-desk/context';
import { ShellCard } from 'src/front-components/theme/page-chrome';
import { DeskButton, ErrorLine } from 'src/front-components/theme/desk-controls';
import { getShellColors, SHELL_SPACE, SHELL_TYPE } from 'src/front-components/theme/shell-tokens';
import { createStudent } from 'src/front-components/utils/lead-desk-data';
import { counsellorOfTelecaller, EMPTY_FORM, findDuplicate, validateStudentForm, type FormErrors, type StudentForm } from 'src/utils/lead-desk';

// Where a new student starts: a counsellor files it to themself, a telecaller to their own counsellor and themself, the admin to nobody yet
// (it goes into the unassigned inbox). The server enforces the same; the form just shows it.
const blank = (ctx: DeskContext): StudentForm => ({
  ...EMPTY_FORM,
  counsellorName: ctx.profile.scope === 'OWN_COUNSELLOR' ? ctx.person : ctx.profile.scope === 'OWN_TELECALLER' ? counsellorOfTelecaller(ctx.people, ctx.person) : '',
  telecallerName: ctx.profile.scope === 'OWN_TELECALLER' ? ctx.person : '',
});

export const AddStudentScreen = ({ ctx }: { ctx: DeskContext }) => {
  const shell = getShellColors(ctx.scheme);
  const [form, setForm] = useState<StudentForm>(blank(ctx));
  const [errors, setErrors] = useState<FormErrors>({});
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const duplicate = findDuplicate(ctx.students, form.phone);

  const save = async (): Promise<void> => {
    const found = validateStudentForm(form);

    setErrors(found);
    setSaved(null);

    if (Object.keys(found).length > 0) return;

    if (duplicate !== null) {
      setFailure(`${duplicate.name ?? 'A student'} already has this mobile number.`);

      return;
    }

    setBusy(true);
    setFailure(null);

    try {
      await createStudent(form);
      await ctx.refresh();
      setSaved(`${form.name.trim()} was added.`);
      setForm(blank(ctx));
      setErrors({});
    } catch (cause) {
      setFailure(cause instanceof Error ? cause.message : 'Could not add the student.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <ShellCard scheme={ctx.scheme}>
      <DeskButton scheme={ctx.scheme} onClick={() => ctx.go('STUDENTS')}>
        ← Back to Students List
      </DeskButton>
      <div style={{ ...SHELL_TYPE.pageTitle, color: shell.text, margin: `${SHELL_SPACE[3]} 0 ${SHELL_SPACE[1]}` }}>Add Student Profile</div>
      <div style={{ ...SHELL_TYPE.body, color: shell.textSecondary, marginBottom: SHELL_SPACE[4] }}>
        Register a new student inquiry or admission lead.{ctx.role === 'ADMIN' ? ' It goes to the next counselor and telecaller in the round robin.' : ''}
      </div>

      {saved !== null && <Notice tone="green">{saved}</Notice>}
      {duplicate !== null && (
        <Notice tone="orange">
          {duplicate.name ?? 'A student'} already has this mobile number. Open them from the list instead of adding a second record.
        </Notice>
      )}

      <StudentFormFields
        scheme={ctx.scheme}
        form={form}
        errors={errors}
        onChange={(patch) => {
          setSaved(null);
          setFailure(null);
          setForm({ ...form, ...patch });
        }}
        profile={ctx.profile}
        counsellors={ctx.counsellors}
        people={ctx.people}
        fixedCounsellor={ctx.role === 'COUNSELLOR' ? ctx.person : ctx.role === 'TELECALLER' ? counsellorOfTelecaller(ctx.people, ctx.person) : undefined}
        fixedTelecaller={ctx.role === 'TELECALLER' ? ctx.person : undefined}
        roundRobin={ctx.role === 'ADMIN'}
      />

      <div style={{ display: 'flex', gap: SHELL_SPACE[2], justifyContent: 'flex-end', alignItems: 'center', borderTop: `1px solid ${shell.borderMedium}`, paddingTop: SHELL_SPACE[4], marginTop: SHELL_SPACE[2] }}>
        <ErrorLine scheme={ctx.scheme} message={failure} />
        <DeskButton scheme={ctx.scheme} onClick={() => { setForm(blank(ctx)); setErrors({}); setFailure(null); setSaved(null); }}>
          Cancel
        </DeskButton>
        <DeskButton scheme={ctx.scheme} primary disabled={busy} onClick={() => void save()}>
          {busy ? 'Saving…' : 'Save'}
        </DeskButton>
      </div>
    </ShellCard>
  );
};
